'use client'

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import {
  EASING_CURVES,
  LOADING_ANIMATION_TIMING,
} from '@/shared/constants/animations'
import { cn } from '@/utils/shadcn/utils'

type WaveType = 'sine' | 'cosine' | 'combined' | 'trending'

interface BarChartSkeletonProps {
  className?: string
  height?: number
  barCount?: number
  barHeight?: number
  barSpacing?: number
  waveType?: WaveType
}

export function BarChartSkeleton({
  className = '',
  height = 246,
  barCount = 12,
  barHeight = 60,
  barSpacing = 8,
  waveType = 'sine',
}: BarChartSkeletonProps) {
  // Initialize with static values for SSR
  const initialBars = Array.from({ length: barCount }, () => barHeight * 0.5)
  const [bars, setBars] = useState<number[]>(initialBars)
  const [isClient, setIsClient] = useState(false)

  // Set client flag after mount
  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Only animate on client side
    if (!isClient) return

    // Generate wave pattern bar heights
    const generateWaveBars = () => {
      const newBars = Array.from({ length: barCount }, (_, index) => {
        const phase = (Date.now() / 1000) * 0.3 // Animate the wave over time
        const x = (index / (barCount - 1)) * 2 * Math.PI // Normalize to 0-2π

        let waveValue: number

        switch (waveType) {
          case 'sine':
            waveValue = Math.sin(x + phase)
            break
          case 'cosine':
            waveValue = Math.cos(x + phase)
            break
          case 'combined':
            // Combine sine and cosine for more complex pattern
            waveValue =
              Math.sin(x + phase) * 0.7 + Math.cos(x * 2 + phase) * 0.3
            break
          case 'trending': {
            // Trending pattern with upward trend
            const trend = (index / (barCount - 1)) * 0.5 // Upward trend
            const wave = Math.sin(x + phase) * 0.5
            waveValue = trend + wave
            break
          }
          default:
            waveValue = Math.sin(x + phase)
        }

        const normalizedValue = (waveValue + 1) / 2 // Normalize to 0-1
        // Ensure bars start from a minimum height (20% of max) instead of zero
        const minHeight = barHeight * 0.4
        const maxHeight = barHeight * 0.9
        return Math.floor(normalizedValue * (maxHeight - minHeight) + minHeight)
      })
      setBars(newBars)
    }

    // Initial generation
    generateWaveBars()

    // Update bars periodically to animate the wave
    const interval = setInterval(
      generateWaveBars,
      LOADING_ANIMATION_TIMING.CHART_SKELETON_UPDATE,
    )

    return () => clearInterval(interval)
  }, [barCount, barHeight, waveType, isClient])

  return (
    <div className={cn('relative', className)} style={{ height }}>
      {/* Chart container */}
      <div className="w-full h-full flex items-end justify-between px-4 pb-4">
        {bars.map((barHeight, index) => (
          <motion.div
            key={index}
            className="bg-moment-blue-grey-200 dark:bg-moment-grey-1000 rounded-t-sm animate-pulse relative overflow-hidden"
            style={{
              width: `${100 / barCount - barSpacing}%`,
              height: barHeight,
            }}
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: barHeight,
              opacity: 1,
            }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: LOADING_ANIMATION_TIMING.BAR_SKELETON_ANIMATE_DURATION,
              delay:
                index * LOADING_ANIMATION_TIMING.BAR_SKELETON_STAGGER_DELAY,
              ease: EASING_CURVES.EASE_OUT_SMOOTH,
            }}
          >
            {/* Subtle glow effect */}
            <motion.div
              className="absolute inset-0 rounded-t-sm bg-gradient-to-t from-moment-blue-grey-100/20 via-moment-blue-grey-100/10 to-transparent dark:from-moment-grey-100 dark:via-moment-grey-100/80 dark:to-moment-grey-100/40"
              animate={{
                opacity: [0.2, 0.6, 0.2],
              }}
              transition={{
                duration: LOADING_ANIMATION_TIMING.BAR_SKELETON_GLOW_DURATION,
                repeat: Infinity,
                delay:
                  index * LOADING_ANIMATION_TIMING.BAR_SKELETON_GLOW_STAGGER,
                ease: 'easeInOut',
              }}
            />
          </motion.div>
        ))}
      </div>
    </div>
  )
}
